import { Box, Button, Chip, CircularProgress, Typography, useMediaQuery, useTheme } from "@mui/material";
import { palette } from "@powerdiary/components-library/lib";
import { useMemo } from "react";
import { CustomFormDto } from "types/generated/models";
import { useDownloadForm } from "../hooks/useDownloadForm";
import { getFormDateInfo } from "./FormItem.helpers";
import { useClientPortalNavigate } from "hooks/useClientPortalRouter";
import { ROUTES } from "../../../routes";
import { useAnalytics } from "hooks/useAnalytics";

interface IFormItemProps {
    form: CustomFormDto;
}

const statusDisplay = {
    Completed: "Completed",
    Expired: "Expired",
    Incomplete: "Incomplete",
};

type Status = {
    label: string;
    color: "success" | "warning" | "default";
};

export default function FormItem({ form }: IFormItemProps) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
    const { downloadForm, isDownloading } = useDownloadForm(form.customFormDataNumber, form.formName);
    const navigateTo = useClientPortalNavigate();
    const { track } = useAnalytics();

    const status: Status = useMemo(
        () => ({
            label:
                form.formStatus === "Locked"
                    ? statusDisplay.Completed
                    : form.isExpired
                      ? statusDisplay.Expired
                      : statusDisplay.Incomplete,
            color: form.formStatus === "Locked" ? "success" : form.isExpired ? "warning" : "default",
        }),
        [form]
    );
    const formDateInfo = getFormDateInfo(form);

    const viewForm = () => {
        navigateTo(`${ROUTES.PUBLIC_FORM.urlPath}/${form.publicId}/${form.customFormDataNumber}`);
        track("form_view_clicked", {
            form_id: form.customFormDataNumber,
            form_name: form.formName,
            form_status: status.label,
        });
    };

    return (
        <Box
            sx={{
                border: `1px solid ${palette.neutral[300]}`,
                borderRadius: "4px",
                justifyContent: "space-between",
                alignItems: isMobile ? "flex-start" : "center",
                flexDirection: isMobile ? "column" : "row",
                display: "flex",
                gap: isMobile ? "16px" : "0px",
                padding: "24px",
            }}
        >
            <Box sx={{ width: "100%" }}>
                <Box>
                    <Typography
                        variant="paragraphLargeBold"
                        fontWeight="bold"
                        color="text.primary"
                        data-test-id="text_form_name"
                    >
                        {form.formName}
                    </Typography>
                    <Chip
                        sx={{ marginLeft: "8px" }}
                        color={status.color}
                        label={status.label}
                        size="small"
                        data-test-id="chip_form_status"
                    />
                </Box>
                <Box
                    sx={{
                        width: "100%",
                        marginTop: "8px",
                        display: "flex",
                        flexDirection: isMobile ? "column" : "row",
                        justifyContent: "space-between",
                        alignItems: isMobile ? "flex-start" : "center",
                        gap: isMobile ? "16px" : "0",
                    }}
                >
                    <Box>
                        {formDateInfo && (
                            <Typography
                                variant="paragraphMediumRegular"
                                color="text.primary"
                                data-test-id="text_form_date_info"
                            >
                                {formDateInfo.label} {formDateInfo.date}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ width: isMobile ? "100%" : "auto" }}>
                        {status.label === statusDisplay.Completed && (
                            <Button
                                sx={{ width: isMobile ? "100%" : "150px" }}
                                variant="contained"
                                size="extraSmall"
                                type="button"
                                onClick={() => void downloadForm()}
                                disabled={isDownloading}
                                startIcon={isDownloading && <CircularProgress size={14} />}
                                data-test-id="button_download"
                            >
                                <Typography variant="paragraphSmallSemiBold">Download</Typography>
                            </Button>
                        )}
                        {status.label === statusDisplay.Incomplete && (
                            <Button
                                sx={{ width: isMobile ? "100%" : "150px" }}
                                variant="contained"
                                size="extraSmall"
                                type="button"
                                onClick={viewForm}
                                data-test-id="button_view"
                            >
                                <Typography variant="paragraphSmallSemiBold">View</Typography>
                            </Button>
                        )}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
